<template>
  <div>
    <!-- Search / Input Card -->
    <b-card class="mb-1">
      <template #header>
        <b-row class="flex-row justify-content-between align-items-center" style="width:100%">
          <b-col>
            <h4 class="mb-0">Account Name / Number Update</h4>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="flat-secondary"
              size="sm"
              class="compact_nav_icon-text-button"
              @click="$router.push({ name: 'account-creation' })"
            >
              <feather-icon icon="ArrowLeftIcon" size="14" style="margin-bottom:0.3rem" />
              <span class="text-nowrap">Back to Accounts</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <hr />

      <validation-observer ref="formValidation">
        <b-form>
          <!-- Account selector -->
          <b-row class="mb-1">
            <b-col cols="12" md="6">
              <b-form-group label="Select Account *">
                <v-select
                  v-model="selectedAccount"
                  :options="allAccounts"
                  :loading="accountsLoading"
                  :get-option-label="(opt) => opt.AccountName + ' (' + opt.AccountNumber + ')'"
                  :filter-by="filterAccount"
                  placeholder="Search by name or account number…"
                  @input="onAccountSelected"
                >
                  <template #option="{ AccountName, AccountNumber }">
                    <span>{{ AccountName }}</span>
                    <small class="text-muted ml-50">{{ AccountNumber }}</small>
                  </template>
                  <template #no-options>
                    <span v-if="accountsLoading">Loading accounts…</span>
                    <span v-else>No accounts found.</span>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="Account Name">
                <b-form-input
                  :value="form.oldAccountName"
                  readonly
                  placeholder="—"
                  class="bg-light"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="Account Number">
                <b-form-input
                  :value="form.oldAccountNumber"
                  readonly
                  placeholder="—"
                  class="bg-light"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 2: New values -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="New Account Name *">
                <validation-provider #default="{ errors }" rules="required" name="New Account Name">
                  <b-form-input
                    v-model="form.newAccountName"
                    placeholder="Replacement account name"
                    :state="errors.length ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="New Account Number *">
                <validation-provider #default="{ errors }" rules="required" name="New Account Number">
                  <b-form-input
                    v-model="form.newAccountNumber"
                    placeholder="Replacement account number"
                    :state="errors.length ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 3: Date range (optional – leave blank to update all dates) -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group
                label="From Date"
                description="Leave blank to include records regardless of date"
              >
                <b-form-input v-model="form.startDate" type="date" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="To Date">
                <b-form-input v-model="form.endDate" type="date" />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Action buttons -->
          <b-row class="mt-1">
            <b-col>
              <b-button
                variant="primary"
                :disabled="previewLoading"
                @click="runPreview"
              >
                <b-spinner v-if="previewLoading" small class="mr-50" />
                <feather-icon v-else icon="SearchIcon" size="14" class="mr-50" />
                Preview Changes
              </b-button>
              <b-button
                v-if="previewResults"
                variant="outline-secondary"
                class="ml-1"
                @click="resetAll"
              >
                Reset
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>

    <!-- No results banner -->
    <b-alert v-if="previewResults && previewResults.grandTotal === 0" show variant="info" class="mt-1">
      <feather-icon icon="InfoIcon" class="mr-50" />
      No matching records found for <strong>{{ form.oldAccountName }}</strong> /
      <strong>{{ form.oldAccountNumber }}</strong> in the selected period.
    </b-alert>

    <!-- Results section -->
    <template v-if="previewResults && previewResults.grandTotal > 0">

      <!-- Summary banner -->
      <b-alert show variant="warning" class="mt-1 mb-1">
        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
        Found <strong>{{ previewResults.grandTotal }}</strong> record(s) across
        <strong>{{ previewResults.results.length }}</strong> table(s) that will be updated from
        <code>{{ form.oldAccountName }} / {{ form.oldAccountNumber }}</code>
        to
        <code>{{ form.newAccountName }} / {{ form.newAccountNumber }}</code>.
        Review each section below before confirming.
      </b-alert>

      <!-- Per-table result cards -->
      <b-card
        v-for="tableResult in previewResults.results"
        :key="tableResult.table"
        class="mb-1"
        no-body
      >
        <b-card-header
          class="d-flex justify-content-between align-items-center py-75 px-1"
          style="cursor:pointer"
          @click="toggleTable(tableResult.table)"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="expandedTables[tableResult.table] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="16"
              class="mr-50 text-secondary"
            />
            <span class="font-weight-bold">{{ tableResult.label }}</span>
            <b-badge variant="danger" class="ml-75">{{ tableResult.count }} records</b-badge>
          </div>
          <small class="text-muted">{{ tableResult.table }}</small>
        </b-card-header>

        <b-collapse :visible="!!expandedTables[tableResult.table]">
          <b-table
            :items="tableResult.records"
            :fields="buildFields(tableResult)"
            small
            responsive
            bordered
            hover
            class="mb-0"
            :tbody-tr-class="rowClass"
          >
            <!-- Matched pairs + AccountCode change preview -->
            <template #cell(_matchedPairs)="{ item }">
              <div>
                <b-badge
                  v-for="pair in item._matchedPairs"
                  :key="pair"
                  variant="warning"
                  class="mr-25"
                  style="font-size:0.7rem"
                >
                  {{ pair }}
                </b-badge>
              </div>
              <div
                v-if="item._codeChanges && Object.keys(item._codeChanges).length > 0"
                class="mt-25"
              >
                <small
                  v-for="(change, col) in item._codeChanges"
                  :key="col"
                  class="d-block text-info"
                  style="font-size:0.7rem"
                >
                  <strong>{{ col }}:</strong>
                  <code>{{ change.old }}</code> → <code>{{ change.new }}</code>
                </small>
              </div>
            </template>
          </b-table>
        </b-collapse>
      </b-card>

      <!-- Confirm / Cancel bar -->
      <b-card class="mt-1">
        <b-row class="align-items-center">
          <b-col cols="auto">
            <b-button
              variant="danger"
              :disabled="confirmLoading"
              @click="showConfirmModal = true"
            >
              <b-spinner v-if="confirmLoading" small class="mr-50" />
              <feather-icon v-else icon="CheckIcon" size="14" class="mr-50" />
              Confirm Update ({{ previewResults.grandTotal }} records)
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button variant="outline-secondary" @click="resetAll">Cancel</b-button>
          </b-col>
          <b-col class="text-muted small">
            This action will update all records shown above and cannot be undone.
          </b-col>
        </b-row>
      </b-card>
    </template>

    <!-- Confirm Modal -->
    <b-modal
      v-model="showConfirmModal"
      title="Confirm Bulk Update"
      ok-title="Yes, Update All"
      ok-variant="danger"
      cancel-title="Cancel"
      centered
      @ok="doConfirm"
    >
      <p>
        You are about to update <strong>{{ previewResults && previewResults.grandTotal }}</strong> record(s):
      </p>
      <ul>
        <li>Account Name: <code>{{ form.oldAccountName }}</code> → <code>{{ form.newAccountName }}</code></li>
        <li>Account Number: <code>{{ form.oldAccountNumber }}</code> → <code>{{ form.newAccountNumber }}</code></li>
        <li v-if="form.startDate || form.endDate">
          Period: {{ form.startDate || '(all)' }} → {{ form.endDate || '(all)' }}
        </li>
      </ul>
      <b-alert show variant="warning">
        <strong>This cannot be undone.</strong> Make sure you have reviewed all records above before proceeding.
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import vSelect from "vue-select";
import store from "@/store";
import accountBulkUpdateStoreModule from "./accountBulkUpdateStoreModule";

const STORE_MODULE = "accountBulkUpdate";

export default {
  name: "AccountBulkUpdate",

  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },

  data() {
    return {
      form: {
        oldAccountName: "",
        oldAccountNumber: "",
        newAccountName: "",
        newAccountNumber: "",
        startDate: "",
        endDate: "",
      },
      selectedAccount: null,
      allAccounts: [],
      accountsLoading: false,
      previewLoading: false,
      confirmLoading: false,
      previewResults: null,
      expandedTables: {},
      showConfirmModal: false,
    };
  },

  beforeCreate() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, accountBulkUpdateStoreModule);
    }
  },

  mounted() {
    this.loadAllAccounts();
  },

  destroyed() {
    if (store.hasModule(STORE_MODULE)) {
      store.unregisterModule(STORE_MODULE);
    }
  },

  methods: {
    loadAllAccounts() {
      this.accountsLoading = true;
      store
        .dispatch(`${STORE_MODULE}/getAllAccountsForBulkUpdate`)
        .then((res) => {
          this.allAccounts = res.data || [];
        })
        .catch((err) => {
          console.error("Failed to load accounts:", err);
        })
        .finally(() => {
          this.accountsLoading = false;
        });
    },

    onAccountSelected(account) {
      if (account) {
        this.form.oldAccountName   = account.AccountName   || "";
        this.form.oldAccountNumber = account.AccountNumber || "";
      }
    },

    filterAccount(option, label, search) {
      const s = (search || "").toLowerCase();
      return (
        (option.AccountName   || "").toLowerCase().includes(s) ||
        (option.AccountNumber || "").toLowerCase().includes(s)
      );
    },

    runPreview() {
      if (!this.form.oldAccountName || !this.form.oldAccountNumber) {
        this.$bvToast.toast("Please select an account using the selector above.", {
          title: "Account Required",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.$refs.formValidation.validate().then((valid) => {
        if (!valid) return;

        this.previewLoading = true;
        this.previewResults = null;
        this.expandedTables = {};

        store
          .dispatch(`${STORE_MODULE}/previewAccountBulkUpdate`, {
            oldAccountName:   this.form.oldAccountName,
            oldAccountNumber: this.form.oldAccountNumber,
            newAccountName:   this.form.newAccountName,
            newAccountNumber: this.form.newAccountNumber,
            startDate:        this.form.startDate || null,
            endDate:          this.form.endDate   || null,
          })
          .then((res) => {
            this.previewResults = res.data;
            // Expand tables that have records
            const expanded = {};
            (res.data.results || []).forEach((r) => {
              expanded[r.table] = true;
            });
            this.expandedTables = expanded;
          })
          .catch((err) => {
            console.error(err);
            this.$bvToast.toast(
              err.response?.data?.error || "Preview failed. Please try again.",
              { title: "Error", variant: "danger", solid: true }
            );
          })
          .finally(() => {
            this.previewLoading = false;
          });
      });
    },

    doConfirm() {
      this.confirmLoading = true;

      store
        .dispatch(`${STORE_MODULE}/confirmAccountBulkUpdate`, {
          oldAccountName:   this.form.oldAccountName,
          oldAccountNumber: this.form.oldAccountNumber,
          newAccountName:   this.form.newAccountName,
          newAccountNumber: this.form.newAccountNumber,
          startDate:        this.form.startDate || null,
          endDate:          this.form.endDate   || null,
        })
        .then((res) => {
          this.$bvToast.toast(res.data.message, {
            title:   "Bulk Update Complete",
            variant: "success",
            solid:   true,
          });
          this.resetAll();
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast(
            err.response?.data?.error || "Update failed. Please try again.",
            { title: "Error", variant: "danger", solid: true }
          );
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },

    toggleTable(tableName) {
      this.$set(this.expandedTables, tableName, !this.expandedTables[tableName]);
    },

    buildFields(tableResult) {
      if (!tableResult.records || tableResult.records.length === 0) return [];

      const sample   = tableResult.records[0];
      const excluded = ["_matchedPairs", "_codeChanges"];
      const dataKeys = Object.keys(sample).filter((k) => !excluded.includes(k));

      const fields = dataKeys.map((key) => ({
        key,
        label:    key,
        sortable: false,
      }));

      fields.push({
        key:   "_matchedPairs",
        label: "Matched Columns",
        sortable: false,
      });

      return fields;
    },

    rowClass(item) {
      if (item && item._matchedPairs && item._matchedPairs.length > 0) {
        return "table-warning";
      }
      return "";
    },

    resetAll() {
      this.form = {
        oldAccountName:  "",
        oldAccountNumber: "",
        newAccountName:  "",
        newAccountNumber: "",
        startDate: "",
        endDate:   "",
      };
      this.previewResults  = null;
      this.expandedTables  = {};
      this.showConfirmModal = false;
      this.$refs.formValidation && this.$refs.formValidation.reset();
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
